import { ModalDetails } from 'business/models/CustomModels';
import { TreeNodeDTO, ResponseType } from 'business/models/Models';
import MiscService from 'business/services/MiscService';
import { RMCustomButtonProps } from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import { IDropDownList } from 'components/customComponents/CommonComponents/RMDropDownList';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import ETreeGroupTypes from 'enums/ETreeGroupTypes';
import ETreeSortTypes from 'enums/ETreeSortTypes';
import EViewType from 'enums/EViewType';
import { exportUsageStatCSV } from 'components/usagestat/UsageStat';
import React, { useCallback, useContext, useMemo } from 'react';
import UsageStatsModal from 'components/usagestat/UsageStatsModal';
import { headerIconSize } from 'utils/Global';
import { useModalHandlers } from 'components/modal';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import ETreeNodeAccessRightNames from 'enums/ETreeNodeAccessRightNames';
import useSendTestEmail, { emailErrorMessage } from 'CustomHooks/useSendTestEmail';
import useSystemBusyCheck from 'CustomHooks/useSystemBusyCheck';
import TreeContext from '../TreeContext';
import { getViewTypeSvgIconName } from '../utils';
import { SearchBar } from '../SearchBar';
import { MainHeaderButtonTitles } from '../constants';
export interface useMainHeaderButtonsProps {
  selectedParentNode: TreeNodeDTO;
  groupBy: ETreeGroupTypes;
  modalDetails: ModalDetails;
  setShowWizard: (flag: boolean) => void;
  setWizardType: (wizardType: string) => void;
  setGroupBy: (groupBy: ETreeGroupTypes) => void;
  setSortBy: (sortBy: ETreeSortTypes) => void;
  setViewType: (viewType: EViewType) => void;
  setModalDetails: (details: ModalDetails) => void;
  rmLite?: boolean;
  newFolderType: ETreeNodeTypes;
  showFilter?: boolean;
}
const headerIconsColor = '#313131';
const miscService = new MiscService();
const useMainHeaderButtons = (props: useMainHeaderButtonsProps) => {
  const {
    selectedParentNode,
    groupBy,
    modalDetails,
    setShowWizard,
    setWizardType,
    setGroupBy,
    setSortBy,
    setViewType,
    setModalDetails,
    rmLite,
    newFolderType,
    showFilter,
  } = props;
  const { hideModal, showLoadingModal } = useModalHandlers();
  const openWizard = useCallback((type: string) => {
    setShowWizard(true);
    setWizardType(type);
  }, []);
  const { checkSystemBusy } = useSystemBusyCheck();
  const { viewType } = useContext(TreeContext);

  const sendTestEmail = useSendTestEmail();

  const sendUsageStatsCSVEmail = useCallback(
    async (timeSpan: number) => {
      setModalDetails({ ...modalDetails, showModal: false });
      showLoadingModal('Sending Email');
      const res = await miscService.sendUsageStatsCSVEmail(timeSpan);
      if (res) {
        let resMessage = '';
        if (ResponseType[res.responseType] === 'Info') {
          resMessage = res.message;
        } else {
          resMessage = emailErrorMessage;
        }
        setModalDetails({
          ...modalDetails,
          showModal: true,
          heading: ResponseType[res.responseType],
          body: resMessage,
          showButtons: true,
        });
        hideModal();
      }
    },
    [modalDetails],
  );

  const handleUsageStatsDownload = useCallback(
    async (timeSpan: number) => {
      setModalDetails({
        ...modalDetails,
        showModal: false,
      });
      exportUsageStatCSV(timeSpan);
    },
    [modalDetails],
  );

  const exportUsageStats = useCallback(async () => {
    setModalDetails({
      ...modalDetails,
      showModal: true,
      heading: 'Usage Statistics',
      body: (
        <UsageStatsModal
          handleUsageStatsDownload={handleUsageStatsDownload}
          sendUsageStatsCSVEmail={sendUsageStatsCSVEmail}
        />
      ),
      showButtons: true,
      okButtonText: 'Close',
      isOkOnly: true,
    });
  }, []);

  const handleGroupingChange = useCallback(
    (groupBy: ETreeGroupTypes) => () => {
      setGroupBy(groupBy);
    },
    [],
  );

  const handleSortingChange = useCallback(
    (sortBy: ETreeSortTypes) => () => {
      setSortBy(sortBy);
    },
    [],
  );

  const handleViewChange = useCallback(
    (viewType: EViewType) => () => {
      setViewType(viewType);
    },
    [],
  );

  const handleBatchloadWizard = () => {
    checkSystemBusy(() => openWizard('BatchloadExperiment'));
  };

  const GroupingList = useMemo(
    () => [
      {
        label: 'None',
        onClick: handleGroupingChange(ETreeGroupTypes.None),
        isDisabled: false,
      },

      {
        label: 'Type',
        onClick: handleGroupingChange(ETreeGroupTypes.Type),
        isDisabled: false,
      },
    ],
    [],
  );

  const SortingList = useMemo(
    () => [
      {
        label: 'Type',
        onClick: handleSortingChange(ETreeSortTypes.Type),
        isDisabled: groupBy.valueOf() === ETreeSortTypes.Type.valueOf(),
      },

      {
        label: 'Name',
        onClick: handleSortingChange(ETreeSortTypes.Name),
        isDisabled: groupBy.valueOf() === ETreeSortTypes.Name.valueOf(),
      },
    ],
    [groupBy],
  );

  const ViewsList = useMemo(
    () => [
      {
        Icon: (
          <RMSvgIcon
            name={getViewTypeSvgIconName(EViewType.ListView)}
            width="20px"
            height="20px"
            fill={headerIconsColor}
          />
        ),
        label: 'List View',
        onClick: handleViewChange(EViewType.ListView),
        isDisabled: false,
      },

      {
        Icon: (
          <RMSvgIcon
            name={getViewTypeSvgIconName(EViewType.ContentView)}
            width="20px"
            height="20px"
            fill={headerIconsColor}
          />
        ),
        label: 'Content View',
        onClick: handleViewChange(EViewType.ContentView),
        isDisabled: false,
      },

      {
        Icon: (
          <RMSvgIcon
            name={getViewTypeSvgIconName(EViewType.ThumbnailView)}
            width="20px"
            height="20px"
            fill={headerIconsColor}
          />
        ),
        label: 'Thumbnail View',
        onClick: handleViewChange(EViewType.ThumbnailView),
        isDisabled: false,
      },

      {
        Icon: (
          <RMSvgIcon
            name={getViewTypeSvgIconName(EViewType.DetailView)}
            width="20px"
            height="20px"
            fill={headerIconsColor}
          />
        ),
        label: 'Detail View',
        onClick: handleViewChange(EViewType.DetailView),
        isDisabled: false,
      },
    ],
    [],
  );

  const WizardList: IDropDownList[] = useMemo(
    () => [
      {
        iconProps: { name: 'ScreenExpWizard', fill: '#414042' },
        label: 'Screen Experiment Wizard',
        isDisabled:
          rmLite ||
          selectedParentNode?.accessRightName !== ETreeNodeAccessRightNames.Modify ||
          newFolderType == ETreeNodeTypes.ProjectsFolder ||
          newFolderType == ETreeNodeTypes.Project,
        onClick: () => openWizard('ScreenExperiment'),
      },
      {
        iconProps: { name: 'BatchloadExperiment', fill: '#414042' },
        label: 'Batchload Experiment',
        onClick: handleBatchloadWizard,
        isDisabled: false,
      },
    ],
    [rmLite, newFolderType, selectedParentNode?.accessRightName],
  );

  const toolsList: IDropDownList[] = useMemo(
    () => [
      {
        label: 'Generate Test Email',
        iconProps: { name: 'Email', fill: '#313131' },
        onClick: sendTestEmail,
      },
      {
        label: 'Usage Statistics',
        iconProps: { name: 'Email', fill: '#313131' },
        onClick: exportUsageStats,
      },
    ],
    [sendTestEmail],
  );

  const headerMainButtons: RMCustomButtonProps[] = [
    {
      iconProps: { name: 'Tools', fill: '#313131', ...headerIconSize },
      title: MainHeaderButtonTitles.Support,
      dropDownList: toolsList,
      color: '#313131',
      showChevronIcon: true,
      chevronIconInitialAngle: 315,
      chevronIconPosition: 'end',
      contentOrientation: 'Vertical',
      contentContainerClass: 'header-button-container main-header-extended-button',
      titleClass: 'header-button-title',
    },
    {
      iconProps: { name: 'Wizard', fill: '#313131', ...headerIconSize },
      title: MainHeaderButtonTitles.Wizard,
      dropDownList: WizardList,
      color: '#313131',
      showChevronIcon: true,
      chevronIconInitialAngle: 315,
      chevronIconPosition: 'end',
      contentOrientation: 'Vertical',
      contentContainerClass: 'header-button-container main-header-extended-button',
      titleClass: 'header-button-title',
    },
  ];

  const headerButtons: Array<RMCustomButtonProps | React.ReactNode> = useMemo(
    () => [
      ...(!rmLite ? headerMainButtons : []),
      showFilter && <SearchBar />,
      {
        title: MainHeaderButtonTitles.GroupBy,
        color: '#313131',
        isDisabled: false,
        dropDownList: GroupingList,
        showCheckMarkOnSelected: true,
        showChevronIcon: true,
        showBorder: true,
        contentContainerClass: 'main-header-tree-action-buttons',
      },

      {
        title: MainHeaderButtonTitles.SortBy,
        color: '#313131',
        isDisabled: false,
        dropDownList: SortingList,
        showCheckMarkOnSelected: true,
        showChevronIcon: true,
        showBorder: true,
        contentContainerClass: 'main-header-tree-action-buttons',
      },

      {
        title: MainHeaderButtonTitles.View,
        iconProps: {
          name: getViewTypeSvgIconName(viewType),
          fill: '#313131',
          ...headerIconSize,
        },
        color: '#313131',
        isDisabled: false,
        dropDownList: ViewsList,
        showChevronIcon: true,
        showBorder: true,
        contentContainerClass: 'main-header-tree-action-buttons',
      },
    ],
    [SortingList, toolsList, selectedParentNode, showFilter, viewType, rmLite],
  );
  return headerButtons;
};

export default useMainHeaderButtons;
